import { useEffect, useRef, useState } from "react";
import { productImageSession } from "../services/productAssetUrls";
export function useProductImageSession() {
  const [scope, setScope] = useState(productImageSession);
  useEffect(() => {
    const sync = () => setScope(productImageSession());
    window.addEventListener("storage", sync); window.addEventListener("palantir-session-updated", sync);
    return () => { window.removeEventListener("storage", sync); window.removeEventListener("palantir-session-updated", sync); };
  }, []);
  return scope;
}
export default function usePrivateProductImage({ identity, load, enabled = true, onFailure }) {
  const timerRef = useRef(null), ref = useRef(null), attempts = useRef(0), current = useRef(null);
  current.current = { load, onFailure };
  const [visible, setVisible] = useState(false), [eager, setEager] = useState(false), [revision, setRevision] = useState(0);
  const [state, setState] = useState({ key: "", url: null });
  const key = `${identity}:${revision}`;
  useEffect(() => { attempts.current = 0; }, [identity]);
  useEffect(() => {
    if (!enabled || !ref.current) return undefined;
    const rect = ref.current.getBoundingClientRect();
    if (typeof IntersectionObserver === "undefined" || (rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth)) { setEager(true); setVisible(true); return undefined; }
    const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { setVisible(true); observer.disconnect(); } }, { rootMargin: "100px" });
    observer.observe(ref.current); return () => observer.disconnect();
  }, [enabled, identity]);
  useEffect(() => {
    if (!visible || !enabled) return undefined;
    let cancelled = false;
    const scope = productImageSession();
    current.current.load({ force: revision > 0 }).then((result) => {
      if (cancelled || scope !== productImageSession()) return;
      setState({ key, url: result.url });
      if (result.url) timerRef.current = setTimeout(() => { attempts.current = 0; setRevision((value) => value + 1); }, Math.max(1000, result.expiresAt - Date.now()));
    }).catch((error) => { if (!cancelled && scope === productImageSession()) { setState({ key, url: null, failed: true }); current.current.onFailure?.(error); } });
    return () => { cancelled = true; clearTimeout(timerRef.current); };
  }, [visible, enabled, key, revision]);
  const onError = () => {
    clearTimeout(timerRef.current);
    setState({ key, url: null, failed: true });
    if (attempts.current++ === 0) setRevision((value) => value + 1);
  };
  return { ref, failed: state.key === key && state.failed, url: enabled && state.key === key ? state.url : null, loading: eager ? "eager" : "lazy", onError };
}
